import { useCallback, useEffect, useRef, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'lp_upi_order';

export function usePendingPayment(visitorId, refreshUsage, setShowPaywall) {
  const [payment, setPayment] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || null; }
    catch { return null; }
  });
  const [paymentError, setPaymentError] = useState('');
  const [checkingPayment, setCheckingPayment] = useState(false);
  const inFlight = useRef(false);
  const savePayment = useCallback((order) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(order));
    setPayment(order);
    setPaymentError('');
  }, []);
  const clearPayment = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setPayment(null);
    setPaymentError('');
  }, []);

  const refreshPayment = useCallback(async () => {
    if (!payment?.order_id || payment.visitor_id !== visitorId || inFlight.current) return;
    inFlight.current = true;
    setCheckingPayment(true);
    try {
      const response = await fetch(`${API}/api/upi/order/${encodeURIComponent(payment.order_id)}`);
      if (!response.ok) throw new Error('Unable to check payment. Please try again.');
      const result = await response.json();
      if (result.visitor_id !== visitorId) throw new Error('This payment belongs to a different device. Contact support.');
      if (result.status === 'approved') {
        const usage = await refreshUsage();
        if (!usage?.is_premium) throw new Error('Payment approved. Access is still updating — please check again.');
        setShowPaywall(false);
      }
      if (result.status !== payment.status) savePayment({ ...payment, status: result.status, subscription_end: result.subscription_end });
      setPaymentError('');
    } catch (error) {
      setPaymentError(error.message || 'Unable to check payment. Please try again.');
    } finally {
      inFlight.current = false;
      setCheckingPayment(false);
    }
  }, [payment, visitorId, refreshUsage, savePayment, setShowPaywall]);

  useEffect(() => {
    if (!payment || payment.visitor_id !== visitorId || ['approved', 'rejected'].includes(payment.status)) return;
    const checkVisible = () => { if (document.visibilityState === 'visible') refreshPayment(); };
    checkVisible();
    const interval = setInterval(checkVisible, 15000);
    window.addEventListener('focus', checkVisible);
    document.addEventListener('visibilitychange', checkVisible);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', checkVisible);
      document.removeEventListener('visibilitychange', checkVisible);
    };
  }, [payment, visitorId, refreshPayment]);

  return { payment: payment?.visitor_id === visitorId ? payment : null, savePayment, clearPayment, refreshPayment, paymentError, checkingPayment };
}